import axios from 'axios';

const API_URLS = {
    production: 'https://centro-infantil-mijardin.onrender.com/api/centro',
    development: 'http://localhost:5000/api/centro'
};

// Determinar las URLs base según el entorno
const API_URL = (process.env.NODE_ENV === 'production' ? API_URLS.production : API_URLS.development).replace(/\/+$/, '');

// Configuración global de axios
axios.defaults.withCredentials = true;

// Función para obtener todos los centros
export const obtenerCentros = async () => {
    try {
        const response = await axios.get(`${API_URL}/recuperar/centros`, {
            headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`
            }
        });
        
        // Verificar si la respuesta tiene la estructura esperada
        if (!response.data) {
            throw new Error('No se recibieron datos del servidor');
        }
        
        // Si la respuesta es un array, devolverlo directamente
        if (Array.isArray(response.data)) {
            return {
                success: true,
                data: response.data
            };
        }
        
        // Si la respuesta tiene la estructura { success, data }
        return response.data;
    } catch (error) {
        console.error('Error al obtener centros:', error);
        return {
            success: false,
            message: error.response?.data?.message || 'Error al obtener los centros'
        };
    }
};

// Función para obtener un centro específico o todos los centros si no se proporciona ID
export const obtenerCentro = async (idCentro) => {
    try {
        // Si no se proporciona un ID, obtener todos los centros
        const endpoint = idCentro ? `/recuperar/centro/${idCentro}` : '/recuperar/centros';
        
        const response = await axios.get(`${API_URL}${endpoint}`, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
        });
        
        if (!response.data) {
            throw new Error('No se recibieron datos del servidor');
        }
        
        // Si se solicitó un centro específico, devolver solo ese centro
        if (idCentro) {
            return {
                success: true,
                data: response.data.data || response.data
            };
        }
        
        // Si se solicitaron todos los centros, devolver el array completo
        return {
            success: true,
            data: Array.isArray(response.data) ? response.data : (response.data.data || [])
        };
    } catch (error) {
        console.error('Error al obtener el centro:', error);
        return {
            success: false,
            message: error.response?.data?.message || 'Error al obtener el centro',
            data: null
        };
    }
};

export const registrarCentro = async (datosCentro) => {
    try {
        const response = await axios.post(`${API_URL}/registrar/centros`, datosCentro, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error al registrar el centro:', error);
        throw error.response?.data || new Error('Error al registrar el centro');
    }
};

export const editarCentro = async (idCentro, datosActualizados) => {
    try {
        const response = await axios.put(`${API_URL}/editar/centros/${idCentro}`, datosActualizados, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error al editar el centro:', error);
        throw error.response?.data || new Error('Error al editar el centro');
    }
};

export const obtenerCuentasBancarias = async () => {
    try {
        const response = await axios.get(`${API_URL}/recuperar/cuentas-bancarias`, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error al obtener cuentas bancarias:', error);
        throw error.response?.data || new Error('Error al obtener cuentas bancarias');
    }
};

export const registrarCuentaBancaria = async (datosCuenta) => {
    try {
        const response = await axios.post(`${API_URL}/registrar/cuentas-bancarias`, datosCuenta, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error al registrar la cuenta bancaria:', error);
        throw error.response?.data || new Error('Error al registrar la cuenta bancaria');
    }
};

export const editarCuentaBancaria = async (idCuenta, datosActualizados) => {
    try {
        const response = await axios.put(`${API_URL}/editar/cuentas-bancarias/${idCuenta}`, datosActualizados, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error al editar la cuenta bancaria:', error);
        throw error.response?.data || new Error('Error al editar la cuenta bancaria');
    }
};

export const eliminarCuentaBancaria = async (idCuenta) => {
    try {
        const response = await axios.delete(`${API_URL}/eliminar/cuentas-bancarias/${idCuenta}`, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error al eliminar la cuenta bancaria:', error);
        throw error.response?.data || new Error('Error al eliminar la cuenta bancaria');
    }
};

// Función para obtener todos los planes
export const obtenerPlanes = async () => {
    try {
        // Para gestión administrativa, obtener todos los planes incluyendo inactivos
        const response = await axios.get(`${API_URL}/recuperar/planes?incluir_inactivos=true`, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error al obtener planes:', error);
        throw error.response?.data || new Error('Error al obtener planes');
    }
};

// Nota: Los planes ya no están asociados a centros específicos
// Usar obtenerPlanes() para obtener todos los planes del sistema

// Función para obtener tipos únicos de planes existentes
export const obtenerTiposPlanes = async () => {
    try {
        const response = await axios.get(`${API_URL}/recuperar/planes?incluir_inactivos=true`, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
        });
        
        // Extraer tipos únicos de los planes existentes
        const planes = response.data.data || [];
        const tiposUnicos = [...new Set(planes.map(plan => plan.tipo).filter(tipo => tipo))];
        
        return tiposUnicos;
    } catch (error) {
        console.error('Error al obtener tipos de planes:', error);
        // Retornar tipos por defecto en caso de error
        return ['matricula', 'mensualidad', 'curso', 'actividad'];
    }
};

// Función para registrar un nuevo plan
export const registrarPlan = async (datosPlan) => {
    try {
        const response = await axios.post(`${API_URL}/registrar/planes`, datosPlan, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error al registrar el plan:', error);
        throw error.response?.data || new Error('Error al registrar el plan');
    }
};

// Función para editar un plan
export const editarPlan = async (idPlan, datosActualizados) => {
    try {
        const response = await axios.put(`${API_URL}/editar/planes/${idPlan}`, datosActualizados, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error al editar el plan:', error);
        throw error.response?.data || new Error('Error al editar el plan');
    }
};

// Función para eliminar un plan
export const eliminarPlan = async (idPlan) => {
    try {
        const response = await axios.delete(`${API_URL}/eliminar/planes/${idPlan}`, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error al eliminar el plan:', error);
        throw error.response?.data || new Error('Error al eliminar el plan');
    }
};

// Función para activar un plan
export const activarPlan = async (idPlan) => {
    try {
        const response = await axios.put(`${API_URL}/activar/planes/${idPlan}`, {}, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error al activar el plan:', error);
        throw error.response?.data || new Error('Error al activar el plan');
    }
};

// Función para desactivar un plan
export const desactivarPlan = async (idPlan) => {
    try {
        const response = await axios.put(`${API_URL}/desactivar/planes/${idPlan}`, {}, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error al desactivar el plan:', error);
        throw error.response?.data || new Error('Error al desactivar el plan');
    }
};
